import { useState, useEffect, useRef, useContext } from 'react';
import Peer from 'peerjs';
import { AppContext } from '../context/AppContext';
import socket from '../services/socketConnection';
import { toast } from "react-toastify";

function RemoteBtn({ userId }) {
  const { appData, remoteScreen, setRemoteScreen } = useContext(AppContext);
  const [peerId, setPeerId] = useState(null);
  const [loading, setLoading] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const pressedKeys = useRef(new Set());

  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 min in seconds
  const timerRef = useRef(null);

  // Send peerId to user
  const sendPeerIdToUser = (userId) => {
    if (peerId && userId) {
      socket.emit("admin:sendPeerId", { userId, peerId, type: "admin" });
    }
  };

  // Setup Peer connection
  useEffect(() => {
    if (!userId) return;

    const url = localStorage.getItem("ipConfig");
    if (!url || Object.keys(appData).length === 0) return;
    if (peerId) return;

    const hostname = new URL(url).hostname;
    const peer = appData.isRemote
      ? new Peer(undefined)
      : new Peer(undefined, {
        host: hostname,              // admin.prabhuchat.com
        port: 443,                   // Always 443 for HTTPS
        secure: true,                // Required
        path: "/screen",      // Full PeerJS path
      });

    peer.on("open", (id) => setPeerId(id));

    peer.on("call", (call) => {
      setRemoteScreen((prev) => ({
        ...prev,
        call,
        modal: true,
        remoteStream: null,
        displays: [],      // list of displays from metadata
        selectedDisplay: null, // currently selected display
      }));
      setLoading(false)
      call.answer(null);
      call.on("stream", (remoteStream) => {
        const displaysMetadata = call.metadata?.displays || [];
        const trackMap = {};

        // Map display_id => video track
        const videoTracks = remoteStream.getVideoTracks();
        displaysMetadata.forEach((d, i) => {
          trackMap[d.display_id] = videoTracks[i];
        });

        // Set remoteScreen state with streams and displays
        setRemoteScreen((prev) => ({
          ...prev,
          remoteStream,
          displays: displaysMetadata,
          selectedDisplay: displaysMetadata[0]?.display_id || null, // default selection
          trackMap,
        }));
      });
      call.on("close", () => {
        setRemoteScreen({
          remoteStream: null,
          call: null,
          modal: false,
          isRemoteControlEnabled: false,
          displays: [],
          selectedDisplay: null,
          trackMap: {},
        });
      });
      call.on("error", (err) => console.error("Call error:", err));
    });

    peer.on("error", (err) => console.error("Peer error:", err));

    return () => peer.destroy();
  }, [userId, JSON.stringify(appData)]);

  // Assign video stream to element
  useEffect(() => {
    if (remoteScreen.modal && videoRef.current && remoteScreen.remoteStream) {
      videoRef.current.srcObject = remoteScreen.remoteStream;
      videoRef.current.play().catch(console.error);
    }
  }, [remoteScreen.modal, remoteScreen.remoteStream]);

  useEffect(() => {
    if (remoteScreen.selectedDisplay && remoteScreen.trackMap && videoRef.current) {
      const track = remoteScreen.trackMap[remoteScreen.selectedDisplay];
      if (track) {
        const stream = new MediaStream([track]);
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => { });
      }
    }
  }, [remoteScreen.selectedDisplay, remoteScreen.trackMap]);

  // Disable context menu
  useEffect(() => {
    const disableContextMenu = (e) => e.preventDefault();
    window.addEventListener("contextmenu", disableContextMenu);
    return () => window.removeEventListener("contextmenu", disableContextMenu);
  }, []);

  useEffect(() => {
    if (remoteScreen.remoteStream) {
      if (appData.advertisment) {
        // unlimited time
        setTimeLeft(null);
      } else {
        // 15 minutes limit
        setTimeLeft(15 * 60);
        timerRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              endCall(); // auto close after 15 min
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [remoteScreen.remoteStream, appData.advertisment]);

  const formatTime = (seconds) => {
    if (seconds === null) return "∞"; // unlimited
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  /** ---------------- Mouse Actions ---------------- **/
  const sendMouse = (e) => {
    if (!remoteScreen.isRemoteControlEnabled || !videoRef.current) return;
    const rect = videoRef.current.getBoundingClientRect();
    const normalizedX = (e.clientX - rect.left) / rect.width;
    const normalizedY = (e.clientY - rect.top) / rect.height;
    socket.emit("mouse-move", { userId: remoteScreen.userId, x: normalizedX, y: normalizedY, displayId: remoteScreen.selectedDisplay });
  };

  const sendRightClick = (e) => {
    e.preventDefault();
    if (!remoteScreen.isRemoteControlEnabled) return;
    socket.emit("mouse-click", { userId: remoteScreen.userId, button: "right", displayId: remoteScreen.selectedDisplay });
  };

  const sendScroll = (e) => {
    if (!remoteScreen.isRemoteControlEnabled) return;
    socket.emit("mouse-scroll", { userId: remoteScreen.userId, deltaX: e.deltaX, deltaY: e.deltaY, displayId: remoteScreen.selectedDisplay });
    e.preventDefault();
  };

  const sendDoubleClick = () => {
    if (!remoteScreen.isRemoteControlEnabled) return;
    socket.emit("mouse-double-click", { userId: remoteScreen.userId, displayId: remoteScreen.selectedDisplay });
  };

  const handleMouseDown = (e) => {
    if (!remoteScreen.isRemoteControlEnabled) return;
    isDraggingRef.current = true;
    socket.emit("mouse-down", {
      userId: remoteScreen.userId,
      button: e.button === 2 ? "right" : e.button === 1 ? "middle" : "left",
      displayId: remoteScreen.selectedDisplay,
    });
  };

  const handleMouseUp = (e) => {
    if (!remoteScreen.isRemoteControlEnabled) return;
    isDraggingRef.current = false;
    socket.emit("mouse-up", {
      userId: remoteScreen.userId,
      button: e.button === 2 ? "right" : e.button === 1 ? "middle" : "left",
      displayId: remoteScreen.selectedDisplay
    });
  };

  const sendMouseDrag = (e) => {
    if (!remoteScreen.isRemoteControlEnabled || !isDraggingRef.current) return;
    sendMouse(e);
  };

  /** ---------------- Keyboard Actions ---------------- **/
  const sendKeyOrShortcut = (e) => {
    if (!remoteScreen.isRemoteControlEnabled) return;
    e.preventDefault();

    const keyMap = {
      ArrowUp: "up",
      ArrowDown: "down",
      ArrowLeft: "left",
      ArrowRight: "right",
      Control: "control",
      Alt: "alt",
      Shift: "shift",
      Enter: "enter",
      Escape: "escape",
      Backspace: "backspace",
      Tab: "tab",
      Meta: "command",
      CapsLock: "capslock",
      AltGraph: "alt",
    };

    const keyName = keyMap[e.key] || e.key.toLowerCase();
    pressedKeys.current.add(keyName);

    if (pressedKeys.current.size > 1) {
      socket.emit("shortcut-keys", { userId: remoteScreen.userId, keys: Array.from(pressedKeys.current), displayId: remoteScreen.selectedDisplay });
      return;
    }

    socket.emit("key-press", { userId: remoteScreen.userId, key: keyName, displayId: remoteScreen.selectedDisplay });
  };

  const clearKey = (e) => {
    const keyMap = {
      Control: "control",
      Alt: "alt",
      Shift: "shift",
      Meta: "command",
      AltGraph: "alt",
    };
    const keyName = keyMap[e.key] || e.key.toLowerCase();
    pressedKeys.current.delete(keyName);
  };

  useEffect(() => {
    window.addEventListener("keydown", sendKeyOrShortcut);
    window.addEventListener("keyup", clearKey);
    return () => {
      window.removeEventListener("keydown", sendKeyOrShortcut);
      window.removeEventListener("keyup", clearKey);
    };
  }, [remoteScreen.userId, remoteScreen.isRemoteControlEnabled]);

  /** ---------------- Button Click ---------------- **/
  const handleTakeRemote = () => {
    if (!peerId || !userId) return;
    setLoading(true)
    setRemoteScreen({ remoteStream: null, call: null, modal: false, userId, isRemoteControlEnabled: false })
    sendPeerIdToUser(userId);
  };

  const endCall = () => {
    remoteScreen?.call?.close();
    setRemoteScreen({
      remoteStream: null,
      userId: null,
      call: null,
      modal: false,
      isRemoteControlEnabled: false,
      displays: [],
      selectedDisplay: null,
      trackMap: {},
    });
  };

  useEffect(() => {
    socket.on("admin:peerDeliveryFailed", (data) => {
      if (data.userId === userId) {
        toast.error(data.reason || "User is offline");
        setLoading(false);
        setRemoteScreen({
          remoteStream: null,
          userId: null,
          call: null,
          modal: false,
          isRemoteControlEnabled: false,
          displays: [],
          selectedDisplay: null,
          trackMap: {},
        });
      }
    });

    return () => socket.off("admin:peerDeliveryFailed");
  }, [userId]);


  return (
    <>
      {/* Take Remote Button */}
      <a onClick={handleTakeRemote} disabled={!peerId}>
        <i className="fa-solid fa-desktop"></i>{loading ? "Loading..." : "Take Remote"}
      </a>

      {/* Maximize button when modal is minimized */}
      {!remoteScreen.modal && remoteScreen.selectedDisplay && (
        <button
          className="btn btn-primary position-fixed"
          style={{ top: "1rem", right: "1rem", zIndex: 1050 }}
          onClick={() => setRemoteScreen({ ...remoteScreen, modal: true })}
        >
          <i className="bi bi-arrows-fullscreen me-1"></i>
          Maximize
        </button>
      )}

      {/* Fullscreen Remote Screen Modal */}
      {remoteScreen.modal && (
        <div
          ref={containerRef}
          className="modal show d-flex align-items-center justify-content-center"
          style={{ display: "flex", zIndex: 1050, backgroundColor: "rgba(0,0,0,0.85)" }}
        >
          <div className="modal-dialog modal-fullscreen">
            <div className="modal-content bg-dark border-light">
              {/* Header */}
              <div className="modal-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  {/* Timer */}
                  <div>
                    <span className="fw-bold text-white me-1">Time Left:</span>
                    <span className="badge bg-warning text-dark fs-6">{formatTime(timeLeft)}</span>
                  </div>

                  {/* Remote Control Toggle */}
                  <div className="form-check form-switch ms-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`remote-control-${remoteScreen.userId}`}
                      checked={remoteScreen.isRemoteControlEnabled}
                      onChange={() =>
                        setRemoteScreen(prev => ({ ...prev, isRemoteControlEnabled: !prev.isRemoteControlEnabled }))
                      }
                    />
                    <label
                      htmlFor={`remote-control-${remoteScreen.userId}`}
                      className="form-check-label"
                      style={{ color: "white" }}
                    >
                      Enable Remote Control
                    </label>
                  </div>

                  {/* Display Selector */}
                  {remoteScreen.displays?.length > 0 && (
                    <div className="d-flex align-items-center ms-3">
                      <label htmlFor="displaySelect" className="me-2 mb-0 fw-semibold" style={{ color: "white" }}>
                        Select Display:
                      </label>
                      <select
                        id="displaySelect"
                        className="form-select form-select-sm"
                        value={remoteScreen.selectedDisplay || ""}
                        onChange={(e) =>
                          setRemoteScreen(prev => ({ ...prev, selectedDisplay: e.target.value }))
                        }
                        style={{ color: "black" }} // option text visible
                      >
                        {remoteScreen.displays.map(d => (
                          <option key={d.display_id} value={d.display_id}>
                            {d.name || d.display_id}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Header Buttons */}
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-light btn-sm"
                    onClick={() => setRemoteScreen(prev => ({ ...prev, modal: false }))}
                  >
                    <i className="bi bi-dash me-1"></i> Minimize
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={endCall}
                  >
                    <i className="bi bi-x-lg me-1"></i> Close
                  </button>
                </div>
              </div>

              {/* Video Body */}
              <div className="modal-body p-0 d-flex justify-content-center align-items-center bg-black">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  controls={false}
                  tabIndex={-1}
                  onWheel={sendScroll}
                  onContextMenu={sendRightClick}
                  onMouseMove={(e) => { sendMouse(e); sendMouseDrag(e); }}
                  onDoubleClick={sendDoubleClick}
                  onMouseUp={handleMouseUp}
                  onMouseDown={handleMouseDown}
                  className="border border-light rounded"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    cursor: remoteScreen.isRemoteControlEnabled ? "crosshair" : "default",
                    pointerEvents: "auto",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>

  );
}

export default RemoteBtn;
